import { useState } from 'react';
import { friendlyErrorMessage } from '../../../shared/friendlyError';
import { EoInput, EoReadOnly, EoSelect, EoYesNo } from '../components/Fields';
import { SectionFrame } from '../components/SectionFrame';
import { employeeOnboardingGateway } from '../index';
import { W4_FILING_STATUSES } from '../types';
import { formatDisplayDate } from '../format';
import type { TaxSectionProps } from './taxProps';
import { moneyError, moneyText, parseMoney } from './taxValidation';

type Version = '' | 'current' | 'historical';

const STATUS_LABELS: Record<string, string> = {
  SingleOrMarriedFilingSeparately: 'Single or Married filing separately',
  MarriedFilingJointly: 'Married filing jointly',
  HeadOfHousehold: 'Head of household',
  Single: 'Single',
  Married: 'Married',
  MarriedWithholdAtHigherSingleRate: 'Married, but withhold at higher Single rate',
};
const STATUS_VALUE_BY_NAME: Record<string, string> = { SingleOrMarriedFilingSeparately: '3', MarriedFilingJointly: '4', HeadOfHousehold: '5' };
const statusToValue = (name: string | undefined): string => (name ? (STATUS_VALUE_BY_NAME[name] ?? '') : '');

interface FederalForm {
  filingStatus: string;
  dependents: string;
  otherIncome: string;
  deductions: string;
  mostRecent: string;
  extra: string;
  multipleJobs: boolean | null;
}
type FederalErrors = Partial<Record<keyof FederalForm, string>>;

/** Federal Income Tax (Figma 01-03). A NEW W-4 is always the current (2020 or later) form; the 2019-or-earlier form is only ever a read-only historical record. */
export function FederalSection({ taxes, refresh, next, back }: TaxSectionProps) {
  const { draft, historical } = taxes.federal;
  const [version, setVersion] = useState<Version>(draft ? 'current' : historical ? 'historical' : '');
  const [form, setForm] = useState<FederalForm>({
    filingStatus: statusToValue(draft?.filingStatus),
    dependents: moneyText(draft?.dependentsAmount),
    otherIncome: moneyText(draft?.otherIncome),
    deductions: moneyText(draft?.deductions),
    mostRecent: moneyText(draft?.mostRecentWithholdingAmount),
    extra: moneyText(draft?.extraWithholding),
    multipleJobs: draft?.multipleJobs ?? null,
  });
  const [errors, setErrors] = useState<FederalErrors>({});
  const [versionError, setVersionError] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = <K extends keyof FederalForm>(key: K, value: FederalForm[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
    // Correcting a field clears its own message right away instead of leaving a stale error under a valid value.
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  function validate(): FederalErrors {
    const e: FederalErrors = {};
    if (!form.filingStatus) e.filingStatus = 'Filing status is required.';
    const dep = moneyError(form.dependents, 'Dependent amount', true);
    if (dep) e.dependents = dep;
    const other = moneyError(form.otherIncome, 'Other income', true);
    if (other) e.otherIncome = other;
    const ded = moneyError(form.deductions, 'Deductions', false);
    if (ded) e.deductions = ded;
    const recent = moneyError(form.mostRecent, 'Most recent withholding amount', false);
    if (recent) e.mostRecent = recent;
    const extra = moneyError(form.extra, 'Extra withholding', false);
    if (extra) e.extra = extra;
    if (form.multipleJobs === null) e.multipleJobs = 'Please answer YES or NO.';
    return e;
  }

  async function submit() {
    setError(null);
    if (version === '') {
      setVersionError('W4 Form Version is required.');
      return;
    }
    if (version === 'historical') {
      // A historical record is read-only: there is nothing to save, so the employee simply continues.
      next();
      return;
    }
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    try {
      const recent = parseMoney(form.mostRecent);
      await employeeOnboardingGateway.saveFederalW4({
        formVersion: 'Y2020OrLater',
        filingStatus: Number(form.filingStatus),
        multipleJobs: form.multipleJobs === true,
        dependentsAmount: parseMoney(form.dependents) ?? 0,
        otherIncome: parseMoney(form.otherIncome) ?? 0,
        deductions: parseMoney(form.deductions) ?? 0,
        extraWithholding: parseMoney(form.extra) ?? 0,
        ...(recent === null ? {} : { mostRecentWithholdingAmount: recent }),
      });
      await refresh();
      next();
    } catch (err) {
      setError(friendlyErrorMessage(err, 'We could not save your W-4 answers. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function clearDraft() {
    setForm({ filingStatus: '', dependents: '', otherIncome: '', deductions: '', mostRecent: '', extra: '', multipleJobs: null });
    setErrors({});
    setError(null);
  }

  const money = (key: 'dependents' | 'otherIncome' | 'deductions' | 'mostRecent' | 'extra', label: string, props: { required?: boolean; hint?: string } = {}) => (
    <EoInput
      label={label}
      {...(props.required ? { required: true } : {})}
      inputMode="decimal"
      autoComplete="off"
      placeholder="00.00"
      value={form[key]}
      error={errors[key]}
      {...(props.hint ? { hint: props.hint } : {})}
      onChange={(e) => set(key, e.target.value)}
    />
  );

  return (
    <SectionFrame
      title="Federal Income Tax"
      onSubmit={submit}
      onBack={back}
      {...(version === 'historical' ? {} : { onClearDraft: clearDraft })}
      busy={busy}
      error={error}
    >
      <div className="eo-grid">
        <div className="eo-grid__span1">
          <EoSelect
            label="W4 Form Version"
            required
            value={version}
            error={versionError}
            onChange={(e) => {
              setVersion(e.target.value as Version);
              setVersionError(undefined);
              setError(null);
            }}
          >
            <option value="">Select</option>
            <option value="current">2020 or Later W-4</option>
            <option value="historical" disabled={!historical}>
              2019 or Earlier W-4
            </option>
          </EoSelect>
        </div>
      </div>
      {!historical && (
        <p className="eo-field__hint" data-testid="w4-legacy-note">
          Use the current W-4 (2020 or later). A new 2019 or earlier W-4 cannot be submitted.
        </p>
      )}
      {version !== '' && <hr className="eo-rule" />}

      {version === 'current' && (
        <>
          <p className="eo-banner eo-banner--info" data-testid="w4-draft-note">
            <strong>Draft / Not submitted.</strong> Your answers are saved as a draft. Your W-4 is not final until you can sign it, and the signature step is
            not available yet.
          </p>
          <div className="eo-grid">
            <EoSelect label="Filing Status" required value={form.filingStatus} error={errors.filingStatus} onChange={(e) => set('filingStatus', e.target.value)}>
              <option value="">Select</option>
              {W4_FILING_STATUSES.map((s) => (
                <option key={s.value} value={String(s.value)}>
                  {s.label}
                </option>
              ))}
            </EoSelect>
            {money('dependents', 'Dependent Amount ($)', { required: true, hint: 'Step 3: claim dependent and other credits.' })}
            {money('otherIncome', 'Other Income ($)', { required: true, hint: 'Step 4(a): other income not from jobs.' })}
            {money('deductions', 'Deductions ($)', { hint: 'Step 4(b): deductions other than the standard deduction.' })}
            {money('mostRecent', 'Most Recent Withholding Amount ($)', { hint: 'For reference only. It is not used to calculate withholding.' })}
            <EoYesNo
              label="Do you have more than one job, or does your spouse also work?"
              required
              value={form.multipleJobs}
              error={errors.multipleJobs}
              onChange={(v) => set('multipleJobs', v)}
            />
            {money('extra', 'Extra Withholding ($)', { hint: 'Step 4(c): extra tax to withhold from each paycheck.' })}
          </div>
        </>
      )}

      {version === 'historical' && historical && (
        <>
          <p className="eo-banner eo-banner--info" data-testid="w4-historical-note">
            This 2019 or earlier W-4 is a <strong>historical record on file</strong> (effective {formatDisplayDate(historical.effectiveFrom)}). It is shown for
            reference and cannot be edited. To make a change, complete the current W-4 (2020 or later).
          </p>
          <div className="eo-grid">
            <EoReadOnly label="Filing Status" value={STATUS_LABELS[historical.filingStatus] ?? historical.filingStatus} />
            <EoReadOnly label="Total Allowances" value={historical.allowances === null ? null : String(historical.allowances)} />
            <EoReadOnly label="Extra Withholding per Pay Period ($)" value={historical.extraWithholding === null ? null : moneyText(historical.extraWithholding)} />
          </div>
          <button type="button" className="eo-link-button eo-link-button--left" onClick={() => setVersion('current')}>
            Complete the current W-4 (2020 or later)
          </button>
        </>
      )}
    </SectionFrame>
  );
}
